import React, { useCallback, useState } from "react";
import { parseSVG } from "svg-path-parser";
import SVGPath from "svgpath";
import "./App.css";
import { GroupMorph, SvgFileData } from "./GroupMorph";

// A simple regex to find the first <path d="...">
const PATH_REGEX = /<path[^>]*\sd=["']([^"']+)["']/i;
const VIEWBOX_REGEX = /<svg[^>]*\sviewBox=["']([^"']+)["']/i;

const extractViewBox = (svg: string) => {
  const viewBoxMatch = VIEWBOX_REGEX.exec(svg);
  const viewBoxString =
    viewBoxMatch && viewBoxMatch[1] ? viewBoxMatch[1] : undefined;
  const minX = viewBoxString ? Number(viewBoxString.split(" ")[0]) : undefined;
  const minY = viewBoxString ? Number(viewBoxString.split(" ")[1]) : undefined;
  const w = viewBoxString ? Number(viewBoxString.split(" ")[2]) : undefined;
  const h = viewBoxString ? Number(viewBoxString.split(" ")[3]) : undefined;

  return { minX, minY, w, h };
};

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (typeof e.target?.result === "string") {
        resolve(e.target.result);
      } else {
        reject(new Error("FileReader result not a string."));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

export const PATH_NORMALIZATION_RADIUS = 78;

/**
 * Converts all H and V commands in an SVG path to C commands representing circular arcs.
 * Also converts all L commands to C commands for consistency.
 * @param pathStr The original SVG path string.
 * @param radius The radius to use for circular arcs when converting H/V commands.
 * @returns The normalized SVG path string with only C commands.
 */
export function normalizePath_offline(
  pathStr: string,
  radius: number = PATH_NORMALIZATION_RADIUS
): string {
  let currentX = 0;
  let currentY = 0;

  return new SVGPath(pathStr)
    .abs() // Convert all commands to absolute positions
    .unshort() // Expand shorthand commands like S and T to their full forms
    .iterate((segment, index, x, y) => {
      const seg = segment as any[];

      // Update current position for 'M' commands
      if (seg[0] === "M") {
        currentX = seg[1];
        currentY = seg[2];
        return;
      }

      // Convert 'H' commands to 'C' commands representing circular arcs
      if (seg[0] === "H") {
        const newX = seg[1];
        const newY = currentY;

        const deltaX = newX - currentX;

        // Calculate control points for a half-circle approximation
        const cp1X = currentX + deltaX / 2;
        const cp1Y = currentY + radius * KAPPA;
        const cp2X = newX - deltaX / 2;
        const cp2Y = newY + radius * KAPPA;
        const endPointX = newX;
        const endPointY = newY;

        // Replace 'H' with 'C' command
        seg[0] = "C";
        seg.splice(
          1,
          seg.length - 1,
          cp1X,
          cp1Y,
          cp2X,
          cp2Y,
          endPointX,
          endPointY
        );

        // Update current position
        currentX = newX;
        currentY = newY;
      }

      // Convert 'V' commands to 'C' commands representing circular arcs
      if (seg[0] === "V") {
        const newX = currentX;
        const newY = seg[1];

        const deltaY = newY - currentY;

        // Calculate control points for a half-circle approximation
        const cp1X = currentX + radius * KAPPA;
        const cp1Y = currentY + deltaY / 2;
        const cp2X = newX + radius * KAPPA;
        const cp2Y = newY - deltaY / 2;
        const endPointX = newX;
        const endPointY = newY;

        // Replace 'V' with 'C' command
        seg[0] = "C";
        seg.splice(
          1,
          seg.length - 1,
          cp1X,
          cp1Y,
          cp2X,
          cp2Y,
          endPointX,
          endPointY
        );

        // Update current position
        currentX = newX;
        currentY = newY;
      }

      // Convert 'L' commands to 'C' commands for consistency
      if (seg[0] === "L") {
        const xEnd = seg[1];
        const yEnd = seg[2];

        // Control points for a straight line approximation
        const cp1X = currentX + (xEnd - currentX) * KAPPA;
        const cp1Y = currentY + (yEnd - currentY) * KAPPA;
        const cp2X = xEnd - (xEnd - currentX) * KAPPA;
        const cp2Y = yEnd - (yEnd - currentY) * KAPPA;
        const endPointX = xEnd;
        const endPointY = yEnd;

        // Replace 'L' with 'C' command
        seg[0] = "C";
        seg.splice(
          1,
          seg.length - 1,
          cp1X,
          cp1Y,
          cp2X,
          cp2Y,
          endPointX,
          endPointY
        );

        // Update current position
        currentX = xEnd;
        currentY = yEnd;
      }

      // Handle other commands if necessary
    })
    .toString();
}

// Approximation constant for circular arcs using cubic Bézier curves
const KAPPA = 0.5522847498;

function normalizePath(path: string) {
  return new SVGPath(path)
    .abs()
    .unshort()
    .iterate((segment, index, x, y) => {
      const seg = segment as any[];

      if (seg[0] === "H") {
        seg[0] = "L";
        seg.push(y);
      } else if (seg[0] === "V") {
        seg[0] = "L";
        seg.splice(1, 0, x);
      }

      // Convert L to C for straight lines
      if (seg[0] === "L") {
        const xEnd = seg[1];
        const yEnd = seg[2];
        // Replace 'L x y' with 'C x y x y x y' to form a straight cubic Bézier
        seg[0] = "C";
        seg.splice(1, seg.length - 1, xEnd, yEnd, xEnd, yEnd, xEnd, yEnd);
      }
    })
    .toString();
}

function App() {
  const [svgFiles, setSvgFiles] = useState<SvgFileData[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!e.dataTransfer.files.length) return;

      // Wait for all async reads
      const newFileData: SvgFileData[] = (
        await Promise.all(
          Array.from(e.dataTransfer.files).map(
            async (file): Promise<SvgFileData | undefined> => {
              const text = await readFileAsText(file);
              const match = PATH_REGEX.exec(text);
              const viewBox = extractViewBox(text);
              if (match && match[1]) {
                const firstPath = match[1];
                const cmds: string[] = [];
                const absPath = normalizePath(firstPath);
                parseSVG(absPath)?.forEach((pathEntry) =>
                  cmds.push(pathEntry.code)
                );
                console.log(
                  "parsedSVG relative (original)",
                  parseSVG(firstPath)
                );
                console.log("parsedSVG absolute", parseSVG(absPath));
                return {
                  path: firstPath,
                  fileName: file.name,
                  signature: cmds.join(" "),
                  absPath,
                  viewBox: `${viewBox.minX} ${viewBox.minY} ${viewBox.w} ${viewBox.h}`,
                };
              } else {
                console.warn(`No <path d="..."> found in file: ${file.name}`);
              }
            }
          )
        )
      ).filter((f): f is SvgFileData => f !== undefined);

      // Update state with new items
      setSvgFiles(newFileData);
    },
    [setSvgFiles, setIsDragOver]
  );

  // Standard drag events to allow dropping
  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(true);
    },
    [setIsDragOver]
  );
  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, [setIsDragOver]);

  // Group by signature
  const groups: Record<string, SvgFileData[]> = {};
  svgFiles.forEach((file) => {
    if (!groups[file.signature]) groups[file.signature] = [];
    groups[file.signature].push(file);
  });

  const groupedEntries = Object.entries(groups);

  return (
    <div
      style={{
        padding: "1rem",
        fontFamily: "sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1rem",
      }}
    >
      <h1>Typograph Testbench</h1>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          width: "400px",
          height: "200px",
          border: "2px dashed #ccc",
          borderColor: isDragOver ? "#00f" : "#ccc",
          borderRadius: "8px",
          marginBottom: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isDragOver ? (
          <p style={{ color: "#000" }}>Drop files here...</p>
        ) : (
          <p>Drag and drop multiple SVG files here</p>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
          justifyContent: "left",
        }}
      >
        {groupedEntries.map(([sig, files], index) => (
          <GroupMorph
            key={index}
            sig={sig}
            files={files}
            baselineSignature={index > 0 ? groupedEntries[0][0] : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
